export interface ScreenplaySaveState {
  status: "saved" | "pending" | "saving" | "error";
  hasUnsavedChanges: boolean;
  lastSavedAt: number | null;
}

/** One writer per open document. Manual saves join autosaves; newer edits are
 * coalesced, never sent concurrently or acknowledged by an older response. */
export class ScreenplaySaveQueue {
  private current: string;
  private saved: string;
  private uncertain = false;
  private running: Promise<boolean> | null = null;
  private timer: ReturnType<typeof setTimeout> | undefined;
  private listeners = new Set<() => void>();
  private state: ScreenplaySaveState = {
    status: "saved", hasUnsavedChanges: false, lastSavedAt: null,
  };

  constructor(initial: string, private write: (snapshot: string) => Promise<void>) {
    this.current = this.saved = initial;
  }

  getSnapshot = () => this.state;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => { this.listeners.delete(listener); };
  };
  private emit(status: ScreenplaySaveState["status"], lastSavedAt = this.state.lastSavedAt) {
    this.state = { status, hasUnsavedChanges: status !== "saved", lastSavedAt };
    this.listeners.forEach((listener) => listener());
  }
  private needsSave() { return this.uncertain || this.current !== this.saved; }

  cancelScheduled = () => {
    clearTimeout(this.timer);
    this.timer = undefined;
  };

  /** Adopt an acknowledged conditional server write, never over a local draft
   * or ambiguous/in-flight save. The UI may now display exactly these bytes
   * without an unconditional autosave echo that could overwrite another tab. */
  adoptAcknowledged(expected: string, value: string): boolean {
    if (this.running || this.uncertain || this.current !== expected || this.saved !== expected) return false;
    this.cancelScheduled(); this.current = this.saved = value;
    this.emit('saved',Date.now());return true;
  }

  update(value: string) {
    const changed = value !== this.current;
    this.current = value;
    this.cancelScheduled();
    if (this.running) return;
    if (!this.needsSave()) {
      if (this.state.status !== "saved") this.emit("saved");
      return;
    }
    // A failed save waits for an edit or an explicit Retry, not a retry loop.
    if (this.state.status === "error" && !changed) return;
    this.emit("pending");
    this.timer = setTimeout(() => { void this.flush(); }, 400);
  }

  flush = (): Promise<boolean> => {
    this.cancelScheduled();
    if (this.running) return this.running;
    if (!this.needsSave()) return Promise.resolve(true);
    // Defer the writer until running is assigned, including synchronous failures.
    this.running = Promise.resolve().then(async () => {
      this.emit("saving");
      try {
        while (this.needsSave()) {
          const snapshot = this.current;
          await this.write(snapshot);
          this.saved = snapshot;
          this.uncertain = false;
          // Keep "saving" if edits arrived while this request was in flight.
          this.emit(this.needsSave() ? "saving" : "saved", Date.now());
        }
        return true;
      } catch {
        // The server may have committed a request whose response was lost.
        // Even undoing to the old value must be retried and acknowledged.
        this.uncertain = true;
        this.emit("error");
        return false;
      } finally {
        this.running = null;
      }
    });
    return this.running;
  };
}
